// Cheap crowd separation via a spatial hash grid: O(n) build, near-constant
// neighbor lookups. Overlapping living characters get pushed apart so squads
// hold shape without real physics.

import type { CharacterState } from './types';

const CELL = 2;
const MIN_DIST = 0.85;
const GRID_OFFSET = 512; // world coords are well within ±512 cells

function cellKey(x: number, z: number): number {
  const cx = Math.floor(x / CELL) + GRID_OFFSET;
  const cz = Math.floor(z / CELL) + GRID_OFFSET;
  return cx * 2048 + cz;
}

export function applySeparation(chars: CharacterState[]): void {
  const grid = new Map<number, CharacterState[]>();
  for (const c of chars) {
    if (!c.alive) continue;
    const key = cellKey(c.x, c.z);
    const bucket = grid.get(key);
    if (bucket) bucket.push(c);
    else grid.set(key, [c]);
  }

  for (const c of chars) {
    if (!c.alive) continue;
    const cx = Math.floor(c.x / CELL) + GRID_OFFSET;
    const cz = Math.floor(c.z / CELL) + GRID_OFFSET;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const bucket = grid.get((cx + dx) * 2048 + (cz + dz));
        if (!bucket) continue;
        for (const other of bucket) {
          if (other.id <= c.id) continue; // each pair once
          if (Math.abs(other.elev - c.elev) > 1.2) continue; // different levels
          const ox = other.x - c.x;
          const oz = other.z - c.z;
          const d = Math.hypot(ox, oz);
          if (d >= MIN_DIST) continue;
          // push both apart along the axis; a deterministic nudge if stacked
          let nx: number;
          let nz: number;
          if (d > 1e-6) {
            nx = ox / d;
            nz = oz / d;
          } else {
            const a = (c.squadIndex * 2.399 + other.squadIndex) % (Math.PI * 2);
            nx = Math.sin(a);
            nz = Math.cos(a);
          }
          const shift = (MIN_DIST - d) / 2;
          c.x -= nx * shift;
          c.z -= nz * shift;
          other.x += nx * shift;
          other.z += nz * shift;
        }
      }
    }
  }
}
